import {getTestProps} from '@benchling/js-test-utils';
import {Box, Loader, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {MOLECULE_IMAGE_POPOVER_QUERY} from 'chemistry/molecules/MoleculeImagePopover';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {isError} from 'graphql-client/Error';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';

import type {
  MoleculeImagePopoverQuery,
  MoleculeImagePopoverQueryVariables,
} from './__generated/MoleculeImagePopoverQuery';

interface AutoCreateBaseCompoundChipProps {
  entityName?: string;
  baseMolfile?: string;
  size?: ChipSize;
}

function useBaseCompoundImage(
  molfile: string | undefined
): SkippableBenchlingQueryResult<MoleculeImagePopoverQuery, MoleculeImagePopoverQueryVariables> {
  return useSkippableBenchlingQuery<MoleculeImagePopoverQuery, MoleculeImagePopoverQueryVariables>(
    MOLECULE_IMAGE_POPOVER_QUERY,
    {variables: {mol: molfile!}, skip: molfile == null}
  );
}

function renderPopoverContent(result: ReturnType<typeof useBaseCompoundImage>): JSX.Element | undefined {
  if (result.type === QueryResultType.SKIPPED) {
    return undefined;
  }
  if (result.type === QueryResultType.PENDING) {
    return (
      <Box sx={{textAlign: 'center', padding: 8}}>
        <Loader size="sm" />
      </Box>
    );
  }
  if (result.type === QueryResultType.ERROR) {
    return undefined;
  }

  const {moleculeImage} = result.data;
  if (isError(moleculeImage)) {
    return <Text size="sm">{moleculeImage.message}</Text>;
  }

  return (
    <Box sx={{textAlign: 'center'}}>
      <Text size="s" color="dimmed" my={4}>
        The molecule compound does not exist yet and will be auto-created as a separate entity with the
        following structure:
      </Text>
      <img
        className={css(styles.structureImg)}
        src={`data:image/svg+xml;base64,${moleculeImage.base64Svg}`}
        alt="Base compound structure"
      />
    </Box>
  );
}

export default function AutoCreateBaseCompoundChip({
  entityName,
  baseMolfile,
  size = ChipSize.SMALL,
}: AutoCreateBaseCompoundChipProps): JSX.Element {
  const text = entityName ? `Base Compound of ${entityName}` : 'New Base Compound';
  const imageResult = useBaseCompoundImage(baseMolfile);
  const popoverContent = renderPopoverContent(imageResult);

  return (
    <span {...getTestProps({component: 'AutoCreateBaseCompoundChip', element: 'root'})}>
      <Chip
        icon="question"
        isReadonly={true}
        size={size}
        text={text}
        tooltipTitle={popoverContent != null ? null : 'A base compound will be auto-created on submit'}
        popoverContent={popoverContent}
        popoverPlacement={FloatingPlacement.TOP}
      />
    </span>
  );
}

const styles = StyleSheet.create({
  structureImg: {
    width: '200px',
    height: '200px',
    display: 'block',
    margin: '0 auto',
  },
});
